import {
  calculateMultiplesValuation,
  type MultiplesValuationInput,
} from "../multiples/calculate";
import { calculateRuleOfThumbValuation } from "../rule-of-thumb/calculate";

/**
 * Calibration backtest harness (WS6).
 *
 * Pure: takes labeled cases (inputs + actual sale price), runs the pure
 * valuation methods, and measures how far each method and the blended
 * synthesis land from reality. No DB, no LLM, no network — callers load
 * cases from wherever they live (SOLD listings, manual fixtures, …).
 *
 * Synthesis weights stay at their defaults until accumulated case volume
 * justifies fitting them — this harness is the flywheel that tells us when
 * and how.
 */

export interface BacktestCaseInputs {
  industry: string;
  zipCode?: string;
  revenue: number;
  sde: number;
  yearsOperating?: number;
  ownerHoursPerWeek?: number;
  volatileCashFlow?: boolean;
  sustainableAdvantage?: boolean;
  inventory?: number;
  equipmentFmv?: number;
}

export interface BacktestCase {
  /** Stable identifier (listing id, fixture name, …). */
  id: string;
  inputs: BacktestCaseInputs;
  actualSalePrice: number;
}

export type BacktestMethod = "multiples" | "ruleOfThumb" | "synthesis";

export interface BacktestCaseResult {
  id: string;
  actualSalePrice: number;
  /** Midpoint estimate per method; null when the method could not run. */
  estimates: Record<BacktestMethod, number | null>;
  /** Signed pct error per method: (estimate − actual) / actual. */
  errors: Record<BacktestMethod, number | null>;
}

export interface MethodErrorStats {
  /** Cases where the method produced an estimate. */
  n: number;
  /** Mean absolute percentage error. */
  mape: number | null;
  /** Median absolute percentage error (robust to outliers). */
  medianAbsPctError: number | null;
  /** Mean signed pct error — persistent bias (over/under valuation). */
  meanSignedPctError: number | null;
}

export interface BacktestSummary {
  caseCount: number;
  perMethod: Record<BacktestMethod, MethodErrorStats>;
  results: BacktestCaseResult[];
}

export interface BacktestOptions {
  /**
   * Synthesis blend weights, normalized over the methods that produced an
   * estimate for each case. Defaults mirror the engine's preference for
   * market multiples over rule-of-thumb sanity ranges.
   */
  weights?: { multiples: number; ruleOfThumb: number };
}

export const DEFAULT_BACKTEST_WEIGHTS = {
  multiples: 0.6,
  ruleOfThumb: 0.4,
} as const;

function runCase(
  testCase: BacktestCase,
  weights: { multiples: number; ruleOfThumb: number },
): BacktestCaseResult {
  const { inputs } = testCase;

  let multiplesMid: number | null = null;
  try {
    const multiplesInput: MultiplesValuationInput = {
      industry: inputs.industry,
      zipCode: inputs.zipCode,
      revenueLastTaxReturn: inputs.revenue,
      profitPlusOwnerCompensation: inputs.sde,
      volatileCashFlow: inputs.volatileCashFlow ?? false,
      ownerHoursPerWeek: inputs.ownerHoursPerWeek ?? 40,
      yearsOperating: inputs.yearsOperating ?? 10,
      sustainableAdvantage: inputs.sustainableAdvantage ?? false,
      skipValidation: true,
      // Backtest cases carry real SDE from closed deals: earnings-led, like
      // the engine.
      sdeVerified: true,
    };
    const result = calculateMultiplesValuation(multiplesInput);
    if (result.valuationMax > 0) {
      multiplesMid = (result.valuationMin + result.valuationMax) / 2;
    }
  } catch {
    multiplesMid = null;
  }

  let ruleOfThumbMid: number | null = null;
  try {
    const result = calculateRuleOfThumbValuation({
      industry: inputs.industry,
      revenue: inputs.revenue,
      sde: inputs.sde,
      inventory: inputs.inventory,
      equipmentFmv: inputs.equipmentFmv,
      sdeVerified: true,
    });
    if (result.valuationMax > 0) {
      ruleOfThumbMid = result.midpoint;
    }
  } catch {
    ruleOfThumbMid = null;
  }

  let synthesis: number | null = null;
  let weightSum = 0;
  let weighted = 0;
  if (multiplesMid != null) {
    weighted += multiplesMid * weights.multiples;
    weightSum += weights.multiples;
  }
  if (ruleOfThumbMid != null) {
    weighted += ruleOfThumbMid * weights.ruleOfThumb;
    weightSum += weights.ruleOfThumb;
  }
  if (weightSum > 0) synthesis = weighted / weightSum;

  const error = (estimate: number | null): number | null =>
    estimate != null && testCase.actualSalePrice > 0
      ? (estimate - testCase.actualSalePrice) / testCase.actualSalePrice
      : null;

  return {
    id: testCase.id,
    actualSalePrice: testCase.actualSalePrice,
    estimates: {
      multiples: multiplesMid,
      ruleOfThumb: ruleOfThumbMid,
      synthesis,
    },
    errors: {
      multiples: error(multiplesMid),
      ruleOfThumb: error(ruleOfThumbMid),
      synthesis: error(synthesis),
    },
  };
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function statsFor(
  results: BacktestCaseResult[],
  method: BacktestMethod,
): MethodErrorStats {
  const errors = results
    .map((r) => r.errors[method])
    .filter((e): e is number => e != null);
  if (errors.length === 0) {
    return { n: 0, mape: null, medianAbsPctError: null, meanSignedPctError: null };
  }
  const absErrors = errors.map((e) => Math.abs(e));
  return {
    n: errors.length,
    mape: absErrors.reduce((a, b) => a + b, 0) / absErrors.length,
    medianAbsPctError: median(absErrors),
    meanSignedPctError: errors.reduce((a, b) => a + b, 0) / errors.length,
  };
}

/** Run the full backtest over labeled cases. */
export function runBacktest(
  cases: BacktestCase[],
  options?: BacktestOptions,
): BacktestSummary {
  const weights = options?.weights ?? DEFAULT_BACKTEST_WEIGHTS;
  const results = cases.map((c) => runCase(c, weights));
  return {
    caseCount: cases.length,
    perMethod: {
      multiples: statsFor(results, "multiples"),
      ruleOfThumb: statsFor(results, "ruleOfThumb"),
      synthesis: statsFor(results, "synthesis"),
    },
    results,
  };
}
